import React from 'react';
import { observer } from 'mobx-react';
import { PodiumFormViewModel } from './podium-form.viewmodel';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { FormFieldInput } from '../../../../../global/form/form-field-input/form-field-input';
import { applyText, cancelText, MORE_BUTTON_TEXT } from '../../../../../../constants/common-labels.constants';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import Text from '../../../../../global/text/text';
import { EnablementStatus } from '../../../../../../constants/common.constants';
import {
    CONTROL_SLIDES_LABEL,
    CONTROL_SLIDES_LINK_TEXT,
    getPodiumAccess,
    PODIUM_ACCESS_TYPE_TEXT,
    PODIUM_ATTENDEES_LABEL,
    PODIUM_CHAT_LABEL,
    PODIUM_ENABLE_TEXT,
    PODIUM_FEATURES_TEXT,
    PODIUM_LINK_TEXT,
    PODIUM_PARTICIPANTS_LABEL,
    PODIUM_QUESTION_ANSWER_LABEL,
    PARTICIPANTS_TAB_LABEL_TEXT,
    PODIUM_ATTENDEES_INFO_MESSAGE,
    PODIUM_PARTICIPANTS_INFO_MESSAGE,
    DISCONNECT_ATTENDEES_LABEL,
    PRESENTER_VIEW_ENABLE_TEXT,
    PRESENTER_VIEW_LINK_TEXT,
} from './podium-form.texts';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { FormattedMessage, useIntl } from 'react-intl';
import { CopyButton } from '../../../../../global/copy-button/copy-button';
import { PodiumAccessType } from '../../../../../../api/podium.dao';
import OECheckbox from '../../../../../global/oe-checkbox/oe-checkbox';
import './podium-form.scss';
import OptionsButton, { IButtonOption } from '../../../../../global/options-button/options-button';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';

export type PodiumFormPresentationProps = {
    vm: PodiumFormViewModel;
};

export const PodiumFormPresentation = observer(({ vm }: PodiumFormPresentationProps): JSX.Element => {
    const intl = useIntl();

    const podiumOptionsButton: IButtonOption[] = [
        {
            label: intl.formatMessage({
                id: `oe.podium.downloadQuestionsAuditLog`,
                description: 'Label text',
                defaultMessage: 'Download Questions Audit Log',
            }),
            action: vm.downloadQuestionsAuditLogCSV
        }
    ];

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form oe-form--podium'>
                    <div className='oe-form__body'>
                        <FormFieldInput.Toggle
                            toggle={vm.togglePodiumEnabled}
                            isSelected={vm.enabled}
                            text={<Text
                                {...PODIUM_ENABLE_TEXT}
                                values={{
                                    suffix: vm.enabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                }}
                            />}
                            className='meeting-toggle'
                        />
                        {vm.enabled && <>
                            <FormField label={intl.formatMessage(PODIUM_LINK_TEXT)}>
                                <div className='flex'>
                                    <input
                                        className='oe-form-field__control'
                                        disabled={true}
                                        value={vm.link}
                                    />
                                    <CopyButton
                                        copyValue={vm.link}
                                        className='copy-podium-link-button'
                                        text='Copy'
                                        withoutIcon
                                        withCopyLink
                                    />
                                </div>
                            </FormField>
                            <FormFieldInput.Toggle
                                toggle={vm.togglePresenterViewEnabled}
                                isSelected={vm.presenterViewEnabled}
                                text={<Text
                                    {...PRESENTER_VIEW_ENABLE_TEXT}
                                    values={{
                                        suffix: vm.presenterViewEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />}
                                className='meeting-toggle presenter-view-toggle'
                            />
                            {vm.presenterViewEnabled && <FormField label={intl.formatMessage(PRESENTER_VIEW_LINK_TEXT)}>
                                <div className='flex'>
                                    <input
                                        className='oe-form-field__control'
                                        disabled={true}
                                        value={vm.presenterViewLink}
                                    />
                                    <CopyButton
                                        copyValue={vm.presenterViewLink}
                                        className='copy-podium-link-button'
                                        text='Copy'
                                        withoutIcon
                                        withCopyLink
                                    />
                                </div>
                            </FormField>}
                            <FormField label={intl.formatMessage(PODIUM_ACCESS_TYPE_TEXT)} >
                                <select
                                    className='oe-form-field__control'
                                    value={vm.accessType}
                                    onChange={e => vm.setAccessType(e.target.value as PodiumAccessType)}
                                >
                                    {getPodiumAccess(intl).map(({ value, label }, i) => {
                                        return <option value={value} key={value + i}>{label}</option>;
                                    })
                                    }
                                </select>
                            </FormField>
                            <div className='podium-features-section'>
                                <div className='podium-features-section__title'>{intl.formatMessage(PODIUM_FEATURES_TEXT)}</div>
                                <div className='podium-features-section__list'>
                                    <div className='podium-features-section__item--with-info'>
                                        <OECheckbox
                                            checked={vm.participantsEnabled}
                                            onChange={() => vm.checkParticipantsFeature()}
                                            label={intl.formatMessage(PODIUM_PARTICIPANTS_LABEL)}
                                            classNames='oe-form-field__checkbox checkbox-filter__checkbox'
                                        />
                                        <InfoPopover>
                                            <div><FormattedMessage {...PODIUM_PARTICIPANTS_INFO_MESSAGE} /></div>
                                        </InfoPopover>
                                    </div>
                                    {vm.participantsEnabled && <FormField label={intl.formatMessage(PARTICIPANTS_TAB_LABEL_TEXT)}>
                                        <input
                                            className='oe-form-field__control'
                                            value={vm.participantsLabel}
                                            onChange={e => vm.setParticipantsLabel(e.target.value)}
                                            onBlur={vm.setParticipantsLabelTouched}
                                        />
                                        {vm.participantsLabelTouched && vm.participantsLabelErrorMessage &&
                                            <span className='oe-form-field__error'>
                                                {vm.participantsLabelErrorMessage}
                                            </span>
                                        }
                                    </FormField>}
                                    <div className='podium-features-section__item--with-info'>
                                        <OECheckbox
                                            checked={vm.attendeesEnabled}
                                            onChange={() => vm.checkAttendeesFeature()}
                                            label={intl.formatMessage(PODIUM_ATTENDEES_LABEL)}
                                            classNames='oe-form-field__checkbox checkbox-filter__checkbox'
                                        />
                                        <InfoPopover>
                                            <div><FormattedMessage {...PODIUM_ATTENDEES_INFO_MESSAGE} /></div>
                                        </InfoPopover>
                                    </div>
                                    <div className='podium-features-section__item--with-info'>
                                        <OECheckbox
                                            checked={vm.disconnectEnabled}
                                            onChange={vm.toggleDisconnectEnabled}
                                            label={intl.formatMessage(DISCONNECT_ATTENDEES_LABEL)}
                                            classNames='oe-form-field__checkbox checkbox-filter__checkbox'
                                            isDisabled={!vm.attendeesEnabled}
                                        />
                                    </div>
                                    <OECheckbox
                                        checked={vm.questionAnswerEnabled}
                                        onChange={() => vm.checkQuestionAnswerFeature()}
                                        label={intl.formatMessage(PODIUM_QUESTION_ANSWER_LABEL)}
                                        classNames='oe-form-field__checkbox checkbox-filter__checkbox'
                                    />
                                    <OECheckbox
                                        checked={vm.chatEnabled}
                                        onChange={() => vm.checkChatFeature()}
                                        label={intl.formatMessage(PODIUM_CHAT_LABEL)}
                                        classNames='oe-form-field__checkbox checkbox-filter__checkbox'
                                    />
                                    <OECheckbox
                                        checked={vm.controlSlidesEnabled}
                                        onChange={() => vm.checkControlSlideFeature()}
                                        label={intl.formatMessage(CONTROL_SLIDES_LABEL)}
                                        classNames='oe-form-field__checkbox checkbox-filter__checkbox'
                                    />
                                </div>
                            </div>

                            {vm.controlSlidesEnabled && <FormField label={intl.formatMessage(CONTROL_SLIDES_LINK_TEXT)}>
                                <div className='flex'>
                                    <input
                                        className='oe-form-field__control'
                                        value={vm.controlSlidesLink}
                                        onChange={e => vm.setControlSlidesLink(e.target.value)}
                                        onBlur={vm.setControlSlidesLinkTouched}
                                    />
                                    <CopyButton
                                        copyValue={vm.controlSlidesLink}
                                        className='copy-podium-link-button'
                                        text='Copy'
                                        withoutIcon
                                        withCopyLink
                                    />
                                </div>
                                {vm.controlSlidesLinkTouched && vm.controlSlidesLinkErrorMessage &&
                                    <span className='oe-form-field__error'>
                                        {vm.controlSlidesLinkErrorMessage}
                                    </span>
                                }
                            </FormField>}
                        </>}
                    </div>
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right' >
                    <DefaultCloseSubWindowButton
                        text={applyText()}
                        modifier={['fat', 'confirm-safe']}
                        onClick={() => vm.updatePodiumDetails()}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                    <OptionsButton
                        className='options-button--right-margin-auto'
                        modifier={['fat', 'options']}
                        label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                        options={podiumOptionsButton}
                        position='top'
                        align='start'
                    />
                </div >
            }
        />
    );
});